import { useAction, useAtom } from '@reatom/npm-react'
import { memo } from 'react'

import { userAtom } from '@root/entities/auth/atoms'
import { openSupportChatAction } from '@root/features/support'
import { supportTicketTotalUnread } from '@root/features/support/atoms/reatom-support-dialog'

import { PulseModal, PulseRow } from './pulse-modal'

/**
 * Where to go when something is wrong.
 *
 * Two real ways through — the support chat in the bot, and the tickets the
 * terminal already keeps, which carry the only live unread count in the app.
 * Nothing here promises a channel that does not exist: there is no bug
 * tracker behind a "report a bug" button, so there is no such button.
 */
export const PulseHelp: React.FC<{ onClose: () => void }> = memo(
  ({ onClose }) => {
    const [user] = useAtom(userAtom)
    const [unread] = useAtom(supportTicketTotalUnread)
    const openChat = useAction(openSupportChatAction)

    const copyId = () => {
      if (user?.delugeId) {
        navigator.clipboard.writeText(user.delugeId).catch(() => undefined)
      }
    }

    return (
      <PulseModal
        title="Get help"
        note="Support reads messages in order. Sending your account id with the first one saves a round trip."
        onClose={onClose}
        width={460}
      >
        <PulseRow
          title="Support chat"
          note="Opens the bot, where the team answers."
        >
          <button className="act" type="button" onClick={() => openChat()}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M21.6 4.4 3.2 11.5c-1 .4-1 1.7.1 2l4.1 1.3 1.6 5c.3.9 1.4 1 2 .3l2.2-2.4 4.3 3.1c.8.6 1.9.2 2.1-.8l3-13.5c.2-1-.8-2-1-2.1Z" />
            </svg>
            Open chat
          </button>
        </PulseRow>

        <PulseRow
          title="Your tickets"
          note={
            unread > 0
              ? `${unread} unread ${unread === 1 ? 'reply' : 'replies'} waiting.`
              : 'Anything you have already raised, and its answers.'
          }
        >
          {unread > 0 ? <span className="dot">{unread}</span> : undefined}
        </PulseRow>

        <PulseRow
          title="Your account id"
          note="Support asks for this to find you."
        >
          <button className="act" type="button" onClick={copyId}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="9" y="9" width="11" height="11" rx="2.5" />
              <path d="M15 6.5V6a2.5 2.5 0 0 0-2.5-2.5H6A2.5 2.5 0 0 0 3.5 6v6.5A2.5 2.5 0 0 0 6 15h.5" />
            </svg>
            Copy
          </button>
        </PulseRow>
      </PulseModal>
    )
  },
)
